import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { Viewport } from '../common/types'
import { getLocalSettings, saveLocalSetting } from '../shared/utils/localStorage'
import {
  CompareLayout,
  CompareLayoutSource,
  ZoomWindow,
  combineAll,
  decodeLayout,
  defaultLayout,
  encodeLayout,
  moveInto,
  moveToNewChart,
  normalizeLayout,
  removeChart,
  parseZoomParams,
  separateAll,
  serializeZoom,
  splitOut,
  zoomWithin,
} from '../utils/compareView'

interface Args {
  // Compare chart is on screen (compare mode with a usable selection)
  isActive: boolean
  // Readings have arrived, so seriesIds is real
  isReady: boolean
  // Selected series, in selection order
  seriesIds: string[]
  // The fetched range; a zoom only lives inside it
  viewport: Viewport
}

/**
 * State for Compare chart groups: which chart each series lives in and the
 * shared zoom window. Lives in ChartsPage because
 * ComparisonChart remounts on every refetch, and mirrors to the /compare URL
 * (layout, zf, zt) so a shared link reproduces the same picture.
 *
 * The starting layout is picked once per compare session from the series
 * count; newly selected series join the first chart instead of reshuffling.
 */
export function useCompareChartView({ isActive, isReady, seriesIds, viewport }: Args) {
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const isCompareRoute = location.pathname === '/compare'

  // Read once from the URL (only on /compare). The layout param can only be
  // decoded against the series ids, which arrive with the readings.
  const [initial] = useState(() => {
    if (!isCompareRoute) return { layout: null, zoom: null }
    return {
      layout: searchParams.get('layout'),
      zoom: parseZoomParams(searchParams.get('zf'), searchParams.get('zt')),
    }
  })
  const urlLayoutPending = useRef(initial.layout)

  const [layoutState, setLayoutState] = useState<CompareLayout | null>(null)
  const [layoutSource, setLayoutSource] = useState<CompareLayoutSource | null>(null)
  const [zoomWindow, setZoomWindow] = useState<ZoomWindow | null>(initial.zoom)
  const [noticeSeen, setNoticeSeen] = useState(() => getLocalSettings().compareSeparateNoticeSeen)

  // Pick the starting layout once, when the first readings of a session arrive
  useEffect(() => {
    if (layoutState !== null || !isActive || !isReady || seriesIds.length === 0) return
    const fromUrl = decodeLayout(urlLayoutPending.current, seriesIds)
    urlLayoutPending.current = null
    if (fromUrl) {
      setLayoutState(fromUrl)
      setLayoutSource('url')
    } else {
      setLayoutState(defaultLayout(seriesIds))
      setLayoutSource('rule')
    }
  }, [layoutState, isActive, isReady, seriesIds])

  // What the charts render: the stored layout fitted to the current selection
  // (or the rule's answer for the first frame before the effect above runs)
  const layout = useMemo(
    () => (layoutState ? normalizeLayout(layoutState, seriesIds) : defaultLayout(seriesIds)),
    [layoutState, seriesIds]
  )

  const update = useCallback(
    (fn: (current: CompareLayout) => CompareLayout) => {
      setLayoutState(fn(layout))
      setLayoutSource('user')
    },
    [layout]
  )

  const actions = useMemo(
    () => ({
      splitOut: (id: string) => update(l => splitOut(l, id)),
      moveInto: (id: string, chartIndex: number) => update(l => moveInto(l, id, chartIndex)),
      moveToNewChart: (id: string) => update(l => moveToNewChart(l, id)),
      removeChart: (chartIndex: number) => update(l => removeChart(l, chartIndex)),
      combineAll: () => update(() => combineAll(seriesIds)),
      separateAll: () => update(() => separateAll(seriesIds)),
    }),
    [update, seriesIds]
  )

  // Leaving the compare view ends the session: the next selection re-applies the rule
  const wasActive = useRef(isActive)
  useEffect(() => {
    if (wasActive.current && !isActive) {
      setLayoutState(null)
      setLayoutSource(null)
      setZoomWindow(null)
    }
    wasActive.current = isActive
  }, [isActive])

  // A new fetched range invalidates a zoom that falls outside it
  const lastViewport = useRef(viewport)
  useEffect(() => {
    const prev = lastViewport.current
    if (prev.minimum === viewport.minimum && prev.maximum === viewport.maximum) return
    lastViewport.current = viewport
    setZoomWindow(z => zoomWithin(z, viewport))
  }, [viewport])

  // Mirror to the URL. A rule-picked layout is left out: the recipient's rule
  // reaches the same answer for the same selection.
  useEffect(() => {
    if (!isCompareRoute || !isActive || layoutState === null) return

    const next = new URLSearchParams(searchParams)

    if (layoutSource !== 'rule' && seriesIds.length > 0) next.set('layout', encodeLayout(layout, seriesIds))
    else next.delete('layout')

    if (zoomWindow) {
      const { zf, zt } = serializeZoom(zoomWindow)
      next.set('zf', zf)
      next.set('zt', zt)
    } else {
      next.delete('zf')
      next.delete('zt')
    }

    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true })
    }
  }, [isCompareRoute, isActive, layoutState, layout, layoutSource, seriesIds, zoomWindow, searchParams, setSearchParams])

  const showSplitNotice = layoutSource === 'rule' && layout.length > 1 && !noticeSeen

  const dismissNotice = useCallback(() => {
    setNoticeSeen(true)
    try {
      saveLocalSetting('compareSeparateNoticeSeen', true)
    } catch {
      // Storage unavailable (private window): the notice just shows again next time
    }
  }, [])

  return {
    layout,
    ...actions,
    zoomWindow,
    setZoomWindow,
    showSplitNotice,
    dismissNotice,
  }
}
